// =============================================================================
// NetClient.ts — Camada de rede do cliente (Socket.IO, global `io` do script).
// - Envia input a 25Hz com número de sequência (o servidor faz ack no snapshot)
// - Guarda credenciais em localStorage p/ reconexão perfeita (F5 sem medo)
// =============================================================================
import type { Socket } from 'socket.io-client';
import {
  MSG,
  type ClientToServerAcks, type ClientToServerPayloads, type JoinAck,
  type LobbyCharPayload, type ReconnectAck, type RematchAck,
  type RoomListAck, type ServerToClientPayloads, type UrnaActionKind,
} from '../../shared/protocol';
import type { PlayerMetaState } from '../../shared/meta';

// Mapas de eventos no formato do socket.io-client, derivados dos mapas
// nome->payload do protocolo: tipar o próprio Socket faz CADA emit/on daqui
// ser checado em compilação contra o contrato de shared/protocol.ts
type NetListenEvents = {
  [K in keyof ServerToClientPayloads]: (data: ServerToClientPayloads[K]) => void;
};
type NetEmitEvents = {
  [K in keyof ClientToServerPayloads]: K extends keyof ClientToServerAcks
    ? undefined extends ClientToServerPayloads[K]
      ? (cb: (res: ClientToServerAcks[K]) => void) => void
      : (payload: ClientToServerPayloads[K], cb: (res: ClientToServerAcks[K]) => void) => void
    : undefined extends ClientToServerPayloads[K]
      ? () => void
      : (payload: ClientToServerPayloads[K]) => void;
};

const STORAGE_KEY = 'bs_session';

export interface StoredSession {
  code: string;
  token: string;
}

export interface MoveVector {
  mx: number;
  my: number;
}

export class NetClient {
  private readonly socket: Socket<NetListenEvents, NetEmitEvents>;
  private seq = 0;
  private inputTimer: ReturnType<typeof setInterval> | null = null;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private lastPing = 0;   // RTT medido (ms) — exibido no placar do TAB

  constructor() {
    // WebSocket direto (sem começar em HTTP long-polling); polling fica só
    // como fallback pra proxy/firewall que bloqueia upgrade
    this.socket = io({ transports: ['websocket', 'polling'] });
  }

  // O payload de cada mensagem vem do mapa ServerToClientPayloads — nada de
  // re-afirmar o tipo à mão nos pontos de escuta (o alargamento pra `Socket`
  // genérico fica confinado aqui: K genérico não resolve o listener condicional)
  on<K extends keyof ServerToClientPayloads>(msg: K, fn: (data: ServerToClientPayloads[K]) => void): void {
    (this.socket as Socket).on(msg as string, fn as (...args: unknown[]) => void);
  }

  // ---- queda e volta da conexão ----------------------------------------------
  // O Socket.IO tenta reconectar sozinho pra sempre (backoff até 5s); esses
  // hooks deixam o app reagir: avisar o eleitor na queda e retomar a sessão
  // quando a conexão voltar (issue #72 — deploy no Coolify congelava o jogo)
  onDisconnect(fn: () => void): void {
    this.socket.on('disconnect', fn);
  }

  onReconnect(fn: () => void): void {
    // 'reconnect' mora no Manager e só dispara em RE-conexões (não na primeira)
    this.socket.io.on('reconnect', fn);
  }

  // ---- entrada em sala ------------------------------------------------------
  // `meta` é o perfil local do ANÔNIMO (#90/#94): logado, o servidor ignora
  create(name: string, charId: string, skinId: string, meta: PlayerMetaState): Promise<JoinAck> {
    return this.emitAck(MSG.ROOM_CREATE, { name, charId, skinId, meta });
  }

  join(code: string, name: string, charId: string, skinId: string, meta: PlayerMetaState): Promise<JoinAck> {
    return this.emitAck(MSG.ROOM_JOIN, { code, name, charId, skinId, meta });
  }

  reconnect(code: string, token: string): Promise<ReconnectAck> {
    return this.emitAck(MSG.ROOM_RECONNECT, { code, token });
  }

  listRooms(): Promise<RoomListAck> {
    return this.emitAck(MSG.ROOM_LIST, {});
  }

  // Botão VOLTAR do comitê: devolve a vaga (o servidor só aceita em lobby)
  leaveRoom(): void { this.socket.emit(MSG.ROOM_LEAVE); }

  // Eleição do Dia/da Semana (#101/#97): sala solo com seed, sem lobby —
  // o game:start chega logo depois do ack
  startEleicao(mode: string, name: string, charId: string): Promise<JoinAck> {
    return this.emitAck(MSG.ELEICAO_START, { mode, name, charId });
  }

  // Payload e resposta amarrados pelos mapas do protocolo: o tipo do ack sai
  // da própria mensagem (ClientToServerAcks), sem <T> escolhido pelo chamador
  private emitAck<K extends keyof ClientToServerAcks & keyof ClientToServerPayloads>(
    msg: K, payload: ClientToServerPayloads[K],
  ): Promise<ClientToServerAcks[K]> {
    return new Promise((resolve) => (this.socket as Socket).emit(msg, payload, resolve));
  }

  // ---- sessão (reconexão pós-F5) ---------------------------------------------
  saveSession(code: string, token: string): void {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ code, token }));
  }

  loadSession(): StoredSession | null {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? 'null');
    } catch {
      return null;
    }
  }

  clearSession(): void {
    localStorage.removeItem(STORAGE_KEY);
  }

  // ---- lobby ----------------------------------------------------------------
  setReady(ready: boolean): void { this.socket.emit(MSG.LOBBY_READY, ready); }
  setChar(charId: string, skinId: string): void {
    const payload: LobbyCharPayload = { charId, skinId };
    this.socket.emit(MSG.LOBBY_CHAR, payload);
  }
  setDifficulty(id: string): void { this.socket.emit(MSG.LOBBY_DIFFICULTY, id); }
  startGame(): void { this.socket.emit(MSG.LOBBY_START); }

  // ---- jogo -----------------------------------------------------------------
  pickUpgrade(idx: number): void { this.socket.emit(MSG.UPGRADE_PICK, { idx }); }
  // Poderes da mesa diretora da urna (issue #100): reroll/banish/skip
  urnaAction(action: UrnaActionKind, idx?: number): void {
    this.socket.emit(MSG.URNA_ACTION, { action, idx });
  }
  togglePause(): void { this.socket.emit(MSG.PAUSE_TOGGLE); }

  /** Easter egg: código secreto digitado no teclado (servidor valida). */
  sendEasterCode(code: string): void {
    this.socket.emit(MSG.EASTER_CODE, { code });
  }

  // O ack garante que o meta:credit do anônimo (#90/#94) já chegou antes do
  // reload da página; servidor mudo não prende o eleitor na saída (timeout)
  quitGame(): Promise<void> {
    return new Promise((resolve) => {
      this.socket.emit(MSG.GAME_QUIT, resolve);
      setTimeout(resolve, 800);
    });
  }
  startInfinite(): void { this.socket.emit(MSG.INFINITE_START); } // MODO INFINITO (#85)

  // Botão REELEIÇÃO do game over (#91): a sala volta ao lobby com a mesma chapa
  requestRematch(): Promise<RematchAck> { return this.emitAck(MSG.LOBBY_REMATCH, {}); }

  // Loop de input fixo em 25Hz (mesmo tick rate do servidor)
  // No F5, esta instância nasce com seq=0, mas o Player retomado preserva o
  // lastSeq no servidor. O snapshot de resume traz esse ack para o próximo
  // input continuar dali, em vez de ser rejeitado como replay indefinidamente.
  syncInputSequence(ack: number | undefined): void {
    if (typeof ack === 'number' && Number.isSafeInteger(ack) && ack > this.seq) this.seq = ack;
  }

  startInputLoop(getInput: () => MoveVector): void {
    this.stopInputLoop();
    this.inputTimer = setInterval(() => {
      const { mx, my } = getInput();
      // volatile: input é estado reenviado a 25Hz — se a conexão engasgar,
      // melhor descartar do que enfileirar inputs velhos (o servidor só usa o último)
      this.socket.volatile.emit(MSG.INPUT, { seq: ++this.seq, mx, my });
    }, 40);
    this.startPingLoop();
  }

  stopInputLoop(): void {
    if (this.inputTimer) clearInterval(this.inputTimer);
    this.inputTimer = null;
    this.stopPingLoop();
  }

  // ---- ping: cronometra o round-trip a cada 2s (ack vazio do servidor) -------
  private startPingLoop(): void {
    this.stopPingLoop();
    const measure = (): void => {
      const t = performance.now();
      this.socket.emit(MSG.PING, () => { this.lastPing = Math.round(performance.now() - t); });
    };
    measure();                                   // primeira leitura imediata
    this.pingTimer = setInterval(measure, 2000);
  }

  private stopPingLoop(): void {
    if (this.pingTimer) clearInterval(this.pingTimer);
    this.pingTimer = null;
  }

  // Último RTT medido em ms (0 até a primeira resposta chegar)
  getPing(): number {
    return this.lastPing;
  }

  // Medição avulsa de RTT (usada pelo mural de salas: como todas as salas
  // moram no mesmo servidor, o "ping da sala" é o seu próprio ping)
  measurePing(): Promise<number> {
    return new Promise((resolve) => {
      const t = performance.now();
      this.socket.emit(MSG.PING, () => {
        this.lastPing = Math.round(performance.now() - t);
        resolve(this.lastPing);
      });
    });
  }
}
